// Thin analytics facade — gtag (GA) + Mixpanel — ported from the deleted
// static/js/analytics.js. All helpers are no-ops if the providers weren't
// initialised (missing env vars), so call sites can fire-and-forget.

import type { PublicConfig } from './config';

type GtagFn = (...args: unknown[]) => void;

type MixpanelApi = {
  init(token: string, opts?: Record<string, unknown>): void;
  track(event: string, props?: Record<string, unknown>): void;
  identify(id: string): void;
  people: { set(props: Record<string, unknown>): void };
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
    mixpanel?: MixpanelApi;
  }
}

const MIXPANEL_CDN = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';

let initialised = false;

// Lazily initialise GA + Mixpanel based on /api/config/ response.
// Safe to call multiple times — second and subsequent calls are no-ops.
export function initAnalytics(config: PublicConfig): void {
  if (initialised) return;
  initialised = true;
  if (config.ga_measurement_id) initGa(config.ga_measurement_id);
  if (config.mixpanel_token) initMixpanel(config.mixpanel_token);
}

function initGa(measurementId: string): void {
  window.dataLayer = window.dataLayer ?? [];
  const gtag: GtagFn = (...args) => {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', measurementId);
  injectScript(
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`,
  );
}

function initMixpanel(token: string): void {
  injectScript(MIXPANEL_CDN, () => {
    try {
      window.mixpanel?.init(token, {
        autocapture: true,
        record_sessions_percent: 100,
      });
    } catch {
      /* swallow — analytics must never break the app */
    }
  });
}

function injectScript(src: string, onload?: () => void): void {
  const el = document.createElement('script');
  el.src = src;
  el.async = true;
  if (onload) el.onload = onload;
  document.head.appendChild(el);
}

// --- Identity ---

export function identify(userId: string | number, email: string): void {
  const id = String(userId);
  try {
    window.mixpanel?.identify(id);
    window.mixpanel?.people.set({ $email: email });
  } catch {
    /* swallow */
  }
  try {
    window.gtag?.('config', 'user_id', { user_id: id });
  } catch {
    /* swallow */
  }
}

// --- Event helpers ---

function track(event: string, props: Record<string, unknown> = {}): void {
  try {
    window.mixpanel?.track(event, props);
  } catch {
    /* swallow */
  }
  try {
    window.gtag?.('event', event, props);
  } catch {
    /* swallow */
  }
}

export function trackHomeView(): void {
  track('home_view');
}

export function trackSignUp(method: string): void {
  track('sign_up', { method });
}

export function trackHeroAdded(heroId: number): void {
  track('hero_added', { hero_id: heroId });
}

export function trackStoryGenerated(
  storyId: number,
  config: Record<string, unknown>,
): void {
  track('story_generated', { story_id: storyId, ...config });
}
